import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertCircle,
  Plus,
  Pencil,
  ShieldCheck,
  Ban,
  MoreHorizontal,
  RefreshCw,
} from 'lucide-react'
import { useCampaigns } from '@/hooks'
import { useForceSyncCampaign } from '@/hooks/use-mutations'
import { REWARD_TYPE, type RewardType } from '@/lib/constants'
import { CampaignDialog } from '@/components/backoffice/campaigns/campaign-dialog'
import { useState } from 'react'
import type { Campaign } from '@/lib/api'

export const Route = createFileRoute('/_app/campaigns')({
  component: CampaignsPage,
})

function formatReward(type: RewardType, value: number): string {
  if (type === REWARD_TYPE.PERCENTAGE) {
    return `${value}%`
  }
  return `R$ ${value.toFixed(2)}`
}

function CampaignCardSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-5 w-16" />
        </div>
        <Skeleton className="h-4 w-60 mt-2" />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-12" />
          </div>
          <div className="space-y-1">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-14" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function CampaignsPage() {
  const { data: campaigns, isLoading, error } = useCampaigns()
  const { mutate: forceSync } = useForceSyncCampaign()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingCampaign, setEditingCampaign] = useState<Campaign | undefined>(undefined)

  const handleCreate = () => {
    setEditingCampaign(undefined)
    setDialogOpen(true)
  }

  const handleEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign)
    setDialogOpen(true)
  }

  const handleForceSync = (id: string) => {
    forceSync(id)
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-red-50 rounded-lg">
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <h3 className="font-medium text-red-900">Erro ao carregar campanhas</h3>
        <p className="text-sm text-red-700 mt-1">
          Não foi possível buscar os dados. Tente novamente.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campanhas"
        description="Gerencie as regras de recompensa e indicação."
        actions={
          <ChonkyButton variant="primary" onClick={handleCreate}>
            <Plus className="h-3.5 w-3.5" />
            Nova Campanha
          </ChonkyButton>
        }
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => <CampaignCardSkeleton key={i} />)
          : campaigns?.map((campaign) => (
              <Card
                key={campaign.id}
                className={!campaign.isActive ? 'opacity-75 bg-muted/40' : ''}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        {campaign.name}
                        {!campaign.isActive && (
                          <Badge variant="secondary" className="text-xs">
                            Inativa
                          </Badge>
                        )}
                      </CardTitle>
                      {campaign.description && (
                        <CardDescription className="mt-1 line-clamp-2">
                          {campaign.description}
                        </CardDescription>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Ações</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(campaign)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleForceSync(campaign.id)}>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Sincronizar Cupons
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-sm">
                    <div>
                      <span className="text-muted-foreground block text-xs mb-0.5">
                        Recompensa
                        <span className="ml-1 font-normal opacity-70 text-[10px] inline-block">
                          ({campaign.rewardType === 'percentage' ? 'Porcentagem' : 'Valor Fixo'})
                        </span>
                      </span>
                      <span className="font-medium text-indigo-600 flex items-center gap-1">
                        {formatReward(campaign.rewardType as RewardType, campaign.rewardValue)}
                      </span>
                    </div>

                    <div>
                      <span className="text-muted-foreground block text-xs mb-0.5">Carência</span>
                      <span className="font-medium flex items-center gap-1">
                        <ShieldCheck className="h-3 w-3" />
                        {campaign.warrantyDays} dias
                      </span>
                    </div>

                    {(campaign.minOrderValue != null || campaign.maxRewardValue != null) && (
                      <div className="col-span-2 pt-2 mt-2 border-t grid grid-cols-2 gap-2">
                        {campaign.minOrderValue != null && (
                          <div>
                            <span className="text-muted-foreground block text-xs mb-0.5">
                              Pedido Mín.
                            </span>
                            <span className="font-medium">
                              R$ {campaign.minOrderValue.toFixed(2)}
                            </span>
                          </div>
                        )}
                        {campaign.maxRewardValue != null && (
                          <div>
                            <span className="text-muted-foreground block text-xs mb-0.5">
                              Teto Recomp.
                            </span>
                            <span className="font-medium flex items-center gap-1 text-amber-600">
                              <Ban className="h-3 w-3" />
                              R$ {campaign.maxRewardValue.toFixed(2)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
      </div>

      <CampaignDialog open={dialogOpen} onOpenChange={setDialogOpen} campaign={editingCampaign} />
    </div>
  )
}
